import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Label,
  Textarea,
  Field,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Lightbulb24Regular,
  CheckmarkCircle24Filled,
  ErrorCircle24Filled,
} from '@fluentui/react-icons';
import { Prompt } from '../types';
import { updatePrompt, UpdatePromptRequest } from '../services/customPromptsService';
import { extractParameters, validateParameterSyntax, formatParameterName } from '../utils/promptParameters';
import { configService } from '../services/configService';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  previewSection: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: '12px',
  },
  previewLabel: {
    fontWeight: '600',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  parameterList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  parameterChip: {
    padding: '4px 8px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
  },
  statusMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '14px',
  },
  successMessage: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  errorMessage: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface EditPromptModalProps {
  open: boolean;
  onClose: () => void;
  prompt: Prompt | null;
  onPromptUpdated?: () => void;
}

export const EditPromptModal: React.FC<EditPromptModalProps> = ({
  open,
  onClose,
  prompt,
  onPromptUpdated,
}) => {
  const styles = useStyles();

  // Form state
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);
  const [detectedParams, setDetectedParams] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Extract parameters when text changes
  useEffect(() => {
    if (text) {
      const syntaxError = validateParameterSyntax(text);
      setValidationError(syntaxError);

      if (!syntaxError) {
        const params = extractParameters(text);
        setDetectedParams(params);
      } else {
        setDetectedParams([]);
      }
    } else {
      setDetectedParams([]);
      setValidationError(null);
    }
  }, [text]);

  // Pre-populate form when prompt changes
  useEffect(() => {
    if (open && prompt) {
      setTitle(prompt.title || '');
      setText(prompt.text || '');
      setCategory(prompt.category || '');
      setMessage(null);
      setValidationError(null);
    }
  }, [open, prompt]);

  const handleSave = async () => {
    if (!prompt) return;

    // Validation
    if (!title.trim()) {
      setMessage({ success: false, text: 'Prompt title is required' });
      return;
    }
    if (!text.trim()) {
      setMessage({ success: false, text: 'Prompt text is required' });
      return;
    }
    if (validationError) {
      setMessage({ success: false, text: validationError });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const promptRequest: UpdatePromptRequest = {
        title: title.trim(),
        text: text.trim(),
        category: category.trim() || 'Custom',
      };

      await updatePrompt(prompt.id, promptRequest);

      setMessage({
        success: true,
        text: `Successfully updated prompt "${title}"`,
      });

      // Clear cache to reload prompts
      configService.clearCache();

      // Notify parent
      if (onPromptUpdated) {
        setTimeout(() => {
          onPromptUpdated();
        }, 1000);
      }

      // Close modal after delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to update prompt:', error);
      setMessage({
        success: false,
        text: error instanceof Error ? error.message : 'Failed to update prompt',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!prompt) return null;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !saving && onClose()}>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              disabled={saving}
            />
          }
        >
          Edit Custom Prompt
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.dialogContent}>
            {/* Prompt Title */}
            <Field label="Prompt Title" required>
              <Input
                value={title}
                onChange={(_, data) => setTitle(data.value)}
                placeholder="e.g., Check Inventory Levels"
                disabled={saving}
                maxLength={100}
              />
            </Field>

            {/* Prompt Text */}
            <Field
              label="Prompt Text"
              required
              hint="Use {variable} for parameters (e.g., {warehouse}, {customer})"
            >
              <Textarea
                value={text}
                onChange={(_, data) => setText(data.value)}
                placeholder="e.g., Show me inventory levels for warehouse {warehouse}"
                disabled={saving}
                rows={4}
                maxLength={2000}
                resize="vertical"
              />
            </Field>

            {/* Parameter Preview */}
            {detectedParams.length > 0 && (
              <div className={styles.previewSection}>
                <div className={styles.previewLabel}>
                  <Lightbulb24Regular />
                  <Text weight="semibold">Detected Parameters</Text>
                </div>
                <div className={styles.parameterList}>
                  {detectedParams.map((param) => (
                    <div key={param} className={styles.parameterChip}>
                      {formatParameterName(param)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category */}
            <Field label="Category" hint="Optional - used for grouping prompts">
              <Input
                value={category}
                onChange={(_, data) => setCategory(data.value)}
                placeholder="e.g., Inventory, Finance"
                disabled={saving}
                maxLength={50}
              />
            </Field>

            {/* Status Message */}
            {message && (
              <div
                className={`${styles.statusMessage} ${
                  message.success ? styles.successMessage : styles.errorMessage
                }`}
              >
                {message.success ? (
                  <CheckmarkCircle24Filled />
                ) : (
                  <ErrorCircle24Filled />
                )}
                <Text>{message.text}</Text>
              </div>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={saving || !!validationError}
          >
            {saving ? <Spinner size="tiny" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
